import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { OarTaskApi } from '../../apis/oar-task-api';
import type { BodyParseTaskApi } from '../../apis/body-parse-task-api';
import type { OarTaskDataLoader } from '../oar-task-data-loader';
import type { OarTaskContent } from '../../types/oar-types';
import { OarParseStatus } from '../../types/oar-types';
import { mapBodySections } from '../../mappers/oar-content-mapper';
import { OarTaskContentModel } from '../../models/oar-task-content-model';

/** Polling interval in milliseconds */
const POLLING_INTERVAL = 5000;

/**
 * Body 数据加载器
 *
 * 负责加载审查意见通知书的正文内容，包括：
 * - 原始正文文本
 * - 结构化的正文段落
 *
 * 工作模式：
 * - 当状态为 SUCCESS 时：调用 getOfficeActionBody 获取解析结果
 * - 当状态为 NOT_STARTED 或其他状态时：使用异步 API 触发解析并轮询结果
 */
@injectable()
export class BodyDataLoader implements OarTaskDataLoader {
    readonly name = 'Body';

    constructor(
        @inject(OarTaskApi)
        private readonly _api: OarTaskApi & BodyParseTaskApi,
        @inject(OarTaskContentModel)
        private readonly _model: OarTaskContentModel,
    ) {}

    /**
     * Body 部分总是需要加载
     */
    shouldLoad(_state: OarTaskContent): boolean {
        return true;
    }

    /**
     * 加载 Body 数据
     *
     * 根据当前解析状态决定执行哪个操作：
     * - SUCCESS: 获取解析结果（调用 getOfficeActionBody）
     * - 其他状态: 使用异步 API 触发解析并轮询结果
     */
    // eslint-disable-next-line complexity
    async load(taskId: string): Promise<Partial<OarTaskContent>> {
        const currentState = this._model.modelData;
        const parseStatus = currentState.oarBodyParseStatus;

        // 根据状态决定调用哪个 API
        if (parseStatus === OarParseStatus.SUCCESS) {
            // 获取已解析的结果
            const bodyInfo = await this._api.getOfficeActionBody({
                path: { task_id: taskId },
            });
            return {
                oarBodyText: bodyInfo.body_text,
                oarBodySections: mapBodySections(bodyInfo.body_sections ?? []),
            };
        }

        // 使用异步 API 触发解析并轮询结果
        const submitResponse = await this._api.parseOfficeActionBodyAsync({
            path: { task_id: taskId },
        });

        const asyncTaskId = submitResponse.async_task_id;

        // 轮询任务状态直到完成
        let isPolling = true;
        while (isPolling) {
            const statusResponse = await this._api.getBodyParseTaskStatus({
                path: { async_task_id: asyncTaskId },
            });

            if (statusResponse.is_finished) {
                isPolling = false;
                if (statusResponse.is_successful) {
                    // 获取结果
                    const resultResponse = await this._api.getBodyParseTaskResult({
                        path: { async_task_id: asyncTaskId },
                    });
                    return {
                        oarBodyText: resultResponse.body_text,
                        oarBodySections: mapBodySections(resultResponse.body_sections ?? []),
                        oarBodyParseStatus: OarParseStatus.SUCCESS,
                    };
                } else {
                    // 任务失败
                    const errorMessage = statusResponse.error_message || '正文解析任务失败';
                    throw new Error(errorMessage);
                }
            }

            // 等待后继续轮询
            await this._waitForDelay(POLLING_INTERVAL);
        }

        // This should never be reached, but TypeScript needs a return statement
        throw new Error('Polling loop exited unexpectedly');
    }

    /**
     * 等待指定时间
     *
     * @param ms - 等待时间（毫秒）
     */
    private _waitForDelay(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
}
